import { Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

interface CheckboxGroupProps<T> {
  name: string;
  label?: string;
  options: T[];
  disabled?: boolean;
  onChange?: (selected: T[]) => void;
  columns?: number;
  labelKey: keyof T;
  valueKey: keyof T;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  exclusiveLabel?: string;
}

export const FormCheckboxGroup = <T,>({
  name,
  options,
  disabled,
  onChange: onChangeProp,
  columns = 2,
  labelKey,
  valueKey,
  xs = 12,
  sm = 12,
  md = 6,
  lg = 4,
  xl = 3,
  exclusiveLabel,
}: CheckboxGroupProps<T>) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        const exclusiveOption = exclusiveLabel
          ? options.find((o) => String(o[labelKey]).toLowerCase() === exclusiveLabel.toLowerCase())
          : undefined;
        const isExclusiveChecked =
          exclusiveOption != null && value.some((v: T) => v[valueKey] === (exclusiveOption as T)[valueKey]);

        const handleChange = (option: T) => (event: React.ChangeEvent<HTMLInputElement>) => {
          const isChecked = event.target.checked;
          const isThisExclusive = exclusiveOption != null && option[valueKey] === exclusiveOption[valueKey];

          let newValue: T[];
          if (isChecked && isThisExclusive) {
            // Selecting the exclusive option clears all others
            newValue = [option];
          } else if (isChecked && isExclusiveChecked) {
            // Selecting any other option removes the exclusive option first
            newValue = [...value.filter((v: T) => v[valueKey] !== exclusiveOption![valueKey]), option];
          } else {
            newValue = isChecked ? [...value, option] : value.filter((v: T) => v[valueKey] !== option[valueKey]);
          }

          onChange(newValue);
          if (onChangeProp) {
            onChangeProp(newValue);
          }
        };

        return (
          <FormGroup>
            <Grid container spacing={1}>
              {options.map((option) => {
                const isThisExclusive =
                  exclusiveOption != null && option[valueKey] === (exclusiveOption as T)[valueKey];
                const isDisabled = disabled || (!isThisExclusive && isExclusiveChecked);

                return (
                  <Grid item lg={lg} xs={xs} md={md} xl={xl} sm={sm} key={String(option[valueKey])}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value.some((v: T) => v[valueKey] === option[valueKey])}
                          onChange={handleChange(option)}
                          disabled={isDisabled}
                        />
                      }
                      label={String(option[labelKey])}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {error?.message && <FormHelperText error>{error.message}</FormHelperText>}
          </FormGroup>
        );
      }}
    />
  );
};
